import { motion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FiEdit2, FiEye, FiEyeOff, FiStar, FiTrash2, FiZap } from 'react-icons/fi'
import Button from '../../../../../../components/ui/Button'
import Card from '../../../../../../components/ui/Card'
import { MenuItem } from '../../../../../../utils/supabaseClient'

interface MobileActionsTabProps {
	cardVariants: {
		hidden: {
			opacity: number;
			y: number;
		};
		visible: {
			opacity: number;
			y: number;
		};
	},
	menuItem: MenuItem,
	togglePublishStatus: () => Promise<void>,
	toggleAvailabilityStatus: () => Promise<void>,
	toggleFeaturedStatus: () => Promise<void>,
	setIsUpdateModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
	setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const MobileActionsTab: React.FC<MobileActionsTabProps> = ({
	cardVariants, menuItem,
	togglePublishStatus, toggleAvailabilityStatus, toggleFeaturedStatus,
	setIsUpdateModalOpen, setIsDeleteModalOpen
}) => {
	const { t } = useTranslation()

	return (
		<motion.div variants={cardVariants}>
			<Card className="p-4 sm:p-6 border-none shadow-md">
				<div className="flex items-center gap-3 mb-4 sm:mb-6">
					<div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
						<FiZap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
					</div>
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						{t('menuItems.quickActions')}
					</h3>
				</div>

				<div className="space-y-3 sm:space-y-4">
					<Button
						variant="outline"
						fullWidth
						leftIcon={menuItem.is_published ? <FiEyeOff /> : <FiEye />}
						onClick={togglePublishStatus}
						className={`justify-start ${!menuItem.is_published
							? 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
							: 'border-gray-600 dark:border-gray-400 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/20'
							}`}
					>
						{menuItem.is_published ? t('menuItems.unpublish') : t('menuItems.publish')}
					</Button>

					<Button
						variant="outline"
						fullWidth
						leftIcon={menuItem.is_available ? <FiEyeOff /> : <FiEye />}
						onClick={toggleAvailabilityStatus}
						className={`justify-start ${!menuItem.is_available
							? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
							: 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
							}`}
					>
						{menuItem.is_available ? t('menuItems.markUnavailable') : t('menuItems.markAvailable')}
					</Button>

					<Button
						variant="outline"
						fullWidth
						leftIcon={<FiStar />}
						onClick={toggleFeaturedStatus}
						className={`justify-start ${menuItem.is_featured
							? 'border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
							: 'hover:border-amber-200 dark:hover:border-amber-800 hover:text-amber-600 dark:hover:text-amber-400'
							}`}
					>
						{menuItem.is_featured ? t('menuItems.unfeature') : t('menuItems.feature')}
					</Button>

					<div className="pt-2 border-t border-gray-200 dark:border-gray-700">
						<Button
							fullWidth
							leftIcon={<FiEdit2 />}
							onClick={() => setIsUpdateModalOpen(true)}
							className="mb-3"
						>
							{t('common.edit')} {t('menuItems.item')}
						</Button>

						<Button
							variant="outline"
							fullWidth
							leftIcon={<FiTrash2 />}
							onClick={() => setIsDeleteModalOpen(true)}
							className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
						>
							{t('common.delete')} {t('menuItems.item')}
						</Button>
					</div>
				</div>
			</Card>
		</motion.div>
	)
}

export default MobileActionsTab